import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import Loader from "../../common/Loader";
import ImageComponent from '../../../components/common/Image';
import { logout } from '../../../slices/auth/authSlice'
import Modal, { ModalHeader, ModalBody, ModalFooter } from '../../common/Modal';
import { fetchAllDetails, getApplicationData, setEligibiltyStep } from '../../../slices/dashboard/dashboardSlice'
import { formatDateToDDMMYYYY } from '../../../utils/functions';
import DatePicker from '../../common/DatePicker';

const ApplicationContent = ({ searchTerm, setSearchTerm, showSearchModal, setShowSearchModal }) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const href = "#";
    const navigationParams = location.state || {};

    const { isLoading } = useSelector((state) => state.dashboard);
    const { userData } = useSelector((state) => state.auth);

    const userName = userData?.response_data?.data?.userName || '';
    const emailID = userData?.response_data?.data?.emailID || '';
    const dealerData = userData?.response_data?.dealer_data || [];

    const [loader, setLoader] = useState(false);
    const [applications, setApplications] = useState([]);
    const [displayedApplications, setDisplayedApplications] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    // Remove local searchTerm state since it's now coming from props
    const [filteredApplications, setFilteredApplications] = useState([]);
    const [selectedDealerCode, setSelectedDealerCode] = useState(navigationParams.dealer_code || 'all');
    const [selectedDealerName, setSelectedDealerName] = useState('All');
    const [tempSelectedDealerCode, setTempSelectedDealerCode] = useState(selectedDealerCode);
    const [tempSelectedDealerName, setTempSelectedDealerName] = useState(selectedDealerName);
    const [showDealerModal, setShowDealerModal] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [showDateModal, setShowDateModal] = useState(false);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('all');

    const statusOptions = [
        { value: 'all', label: 'All Status' },
        { value: 'in-progress', label: 'In Progress' },
        { value: 'approved', label: 'Approved' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: 'disbursed', label: 'Disbursed' },
        { value: 'rejected', label: 'Rejected' }
    ];

    const recordsPerPage = 4;

    // Function to fetch all details
    const fetchApplicationData = () => {

        setLoader(true);

        const apiParams = {
            from_dt: navigationParams?.from_dt,
            to_dt: navigationParams?.to_dt,
            status: navigationParams.status || 'all',

            dealer_code: selectedDealerCode
        };

        dispatch(fetchAllDetails(apiParams))
            .unwrap()
            .then((res) => {
                if (res?.response_code === "S100") {
                    const fetchedApplications = res?.response_data || [];
                    setApplications(fetchedApplications);
                    setCurrentPage(1);
                    setDisplayedApplications(fetchedApplications.slice(0, recordsPerPage));
                } else {
                    toast.error(res?.response_string);
                }
            })
            .catch((error) => {
                toast.error(error?.message || 'Failed to fetch applications');
            })
            .finally(() => {
                setLoader(false);
            });
    };

    useEffect(() => {
        const today = new Date();

        // Format to "YYYY-MM-DD" for UI
        const formatDate = (date) => {
            const yyyy = date.getFullYear();
            const mm = String(date.getMonth() + 1).padStart(2, '0');
            const dd = String(date.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        };

        const twoMonthsAgo = new Date(today);
        twoMonthsAgo.setMonth(today.getMonth() - 2);

        setDateFrom(formatDate(today));         // today
        setDateTo(formatDate(twoMonthsAgo));    // 2 months ago

    }, []);

    // Add this function with your other functions
    const handleProceedFilter = () => {
        // Validate date range
        const fromDate = new Date(dateFrom);
        const toDate = new Date(dateTo);
        if (fromDate > toDate) {
            toast.warning('Invalid Date Selection');
            // Reset dates to current date but keep modal open
            const now = new Date();
            const todayStr = now.toISOString().slice(0, 10);
            setDateFrom(todayStr);
            setDateTo(todayStr);
            return;
        }
        // Pass date with time in payload
        const apiParams = {
            from_dt: `${dateFrom} 00:00:00`,
            to_dt: `${dateTo} 00:00:00`,
            status: selectedStatus,
            dealer_code: selectedDealerCode
        };

        setLoader(true);
        dispatch(fetchAllDetails(apiParams))
            .unwrap()
            .then((res) => {
                if (res?.response_code === "S100") {
                    const fetchedApplications = res?.response_data || [];
                    setApplications(fetchedApplications);
                    setCurrentPage(1);
                    setDisplayedApplications(fetchedApplications.slice(0, recordsPerPage));
                } else {
                    toast.error(res?.response_string);
                }
            })
            .catch((error) => {
                toast.error(error?.message || 'Failed to fetch applications');
            })
            .finally(() => {
                setLoader(false);
            });
        setShowDateModal(false);
    };

    // Function to handle dealer selection
    const handleDealerSelection = (dealerCode, dealerName) => {
        setTempSelectedDealerCode(dealerCode);
        setTempSelectedDealerName(dealerName);
    };

    const handleApplyDealer = () => {
        setSelectedDealerCode(tempSelectedDealerCode);
        setSelectedDealerName(tempSelectedDealerName);
        setShowDealerModal(false);
    };

    // Function to load more records
    const loadMoreRecords = () => {
        const nextPage = currentPage + 1;
        const startIndex = 0;
        const endIndex = nextPage * recordsPerPage;

        setDisplayedApplications(filteredApplications.slice(startIndex, endIndex));
        setCurrentPage(nextPage);
    };

    // Check if there are more records to load
    const hasMoreRecords = displayedApplications.length < filteredApplications.length;

    useEffect(() => {
        fetchApplicationData();
    }, [selectedDealerCode]);

    useEffect(() => {
        const startIndex = 0;
        const endIndex = currentPage * recordsPerPage;
        setDisplayedApplications(filteredApplications.slice(startIndex, endIndex));
    }, [filteredApplications, currentPage]);

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredApplications(applications);
        } else {
            const filtered = applications.filter((application) =>
                application.reference_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                application.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                application.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                application.mobile_number?.includes(searchTerm) ||
                `${application.first_name} ${application.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredApplications(filtered);
        }
        setCurrentPage(1);
    }, [searchTerm, applications]);

    // Function to get status ribbon class
    const getStatusClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'inprogress':
                return 'inprogress';
            case 'approved':
                return 'completed';
            case 'cancelled':
                return 'Reschdule';
            case 'disbursed':
                return 'completed';
            case 'rejected':
                return 'reject';
            default:
                return 'inprogress';
        }
    };

    const getData = async (agreementNo, status, stage) => {
        setLoader(false)
        if (status?.toLowerCase() === 'in-progress') {
            setLoader(true)
            const response = await dispatch(getApplicationData({ "reference_id": agreementNo, stage: stage }))
            setLoader(false)
            if (response?.payload?.response_code === 'S100') {
                navigate('/eligiblity')
            }
            else {
                !response?.payload?.error &&
                toast.error(response?.payload?.response_string, {
                    position: 'top-right',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined
                });
            }
        }
        else if (status?.toLowerCase() === 'approved') {
            navigate(`/details?referenceId=${agreementNo}`)
        }
        else if (status?.toLowerCase() === 'cancelled') {
            navigate(`/details?referenceId=${agreementNo}`)
        }
        else if (status?.toLowerCase() === 'rejected') {
            navigate(`/details?referenceId=${agreementNo}`)
        }
        else if (status?.toLowerCase() === 'hold') {
            navigate(`/details?referenceId=${agreementNo}`)
        }
        else if(status?.toLowerCase() === 'disbursed'){
            navigate(`/details?referenceId=${agreementNo}`)
        }
        else if(status?.toLowerCase() === 'queue'){
            navigate(`/details?referenceId=${agreementNo}`)
        }
    }

    // Function to capitalize first letter of status
    const capitalizeFirstLetter = (str) => {
        if (!str) return str;
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    const handleLogout = () => {
        dispatch(logout());
        navigate('/login');
        setShowProfileModal(false);
    };

    const handleNavigateToDashboard = () => {
        navigate('/dashboard');
    };

    return (
        <>
            <div className="container-fluid kyc-tab-dash-app dashboard-bg height-scroll">

                {loader === true && <Loader />}
                <div className="row">
                    <div className="col-md-12 ">
                        <label htmlFor="dealer-select" className="form-label">Stores</label>
                        <div className="d-flex align-items-center gap-2">
                            <div className="has-icon w-100"
                                onClick={() => setShowDealerModal(true)}
                                onKeyDown={(e) => e.key === 'Enter' && setShowDealerModal(true)}
                                tabIndex={0}
                                role="button">
                                <input
                                    className="form-control"
                                    placeholder="All"
                                    type="text"
                                    value={selectedDealerName}
                                    readOnly
                                />
                                <i className="icon-arrowdown-L1 custom-app-arrow" style={{ cursor: 'pointer' }}></i>
                            </div>
                            <div className="filter-tab" onClick={() => setShowDateModal(true)}
                                onKeyDown={(e) => e.key === 'Enter' && setShowDateModal(true)}
                                tabIndex={0}
                                role="button">
                                <i className="icon-settingh text-white fw-bold"></i>
                            </div>
                        </div>
                        <p className="text-secondary mt-3 text-uppercase mb-0">
                            Total Applications: <span className="fw-bold">{filteredApplications.length}</span>
                            {searchTerm && applications.length !== filteredApplications.length && (
                                <span className="text-muted d-none"> (filtered from {applications.length})</span>
                            )}
                        </p>
                        {displayedApplications.length > 0 && displayedApplications.length < filteredApplications.length && (
                            <p className="text-secondary small mb-0">
                                Showing {displayedApplications.length} of {filteredApplications.length} applications
                            </p>
                        )}
                    </div>
                </div>
                <div className="mb-2">
                    <div className="row gy-3">
                        <p className="text-secondary mt-3 text-uppercase">
                            {searchTerm && applications.length !== filteredApplications.length && (
                                <span className="text-muted d-none"> (filtered from {applications.length})</span>
                            )}
                        </p>
                        {displayedApplications.length > 0 ? (
                            displayedApplications.map((application, index) => (
                                <div key={application.reference_id || index} className="col-md-12">

                                    <div className="db-item border-0 position-relative application-list"
                                        onClick={() => { getData(application?.reference_id, application?.status, application?.stage) }}
                                        onKeyDown={(e) => e.key === 'Enter' && getData(application?.reference_id, application?.status, application?.stage)}
                                        tabIndex={0}
                                        role="button">
                                        <div className="name-detail-app align-items-center">
                                            <div>
                                                <p className="fw-semibold mb-0 small">
                                                    {(() => {
                                                        const firstName = application.first_name?.trim();
                                                        const lastName = application.last_name?.trim();

                                                        if (firstName && lastName) {
                                                            return `${firstName} ${lastName}`;
                                                        } else if (firstName || lastName) {
                                                            return firstName || lastName;
                                                        } else {
                                                            return "-";
                                                        }
                                                    })()}
                                                </p>
                                                <ul>
                                                    <li className="small text-body-secondary">
                                                        {application.reference_id}
                                                    </li>
                                                </ul>
                                            </div>
                                            <div>
                                                <span className="badge consumer-D text-white fs-6">
                                                    CD
                                                </span>
                                            </div>
                                        </div>
                                        <div className={`ribbon ${getStatusClass(application.status)}`}>
                                            {capitalizeFirstLetter(application?.status)}
                                        </div>

                                        <div className="dash-table dash-table-app">
                                            <div className="row app-card w-100">
                                                <div className="col-4">
                                                    <p>
                                                        <label className="fw-normal">Mobile No</label>
                                                        <p className="text-body-secondary fw-semibold">
                                                            {application?.mobile_number}
                                                        </p>
                                                    </p>
                                                </div>

                                                <div className='col-4 text-center'></div>

                                                <div className="col-4 text-end">
                                                    <p>
                                                        <label className="fw-normal">Created Date</label>
                                                        <p className="text-body-secondary fw-semibold">
                                                            {formatDateToDDMMYYYY(application?.created_date_time)}
                                                        </p>
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="row app-card pb-0 w-100">
                                                {/* App. Amt column */}
                                                {application?.approval_amt !== undefined && application?.approval_amt !== null && application?.approval_amt !== '' && (
                                                    <div className={
                                                        (application?.brand !== undefined && application?.brand !== null && application?.brand !== '') &&
                                                            (application?.price !== undefined && application?.price !== null && application?.price !== '')
                                                            ? "col-4"
                                                            : ((application?.brand !== undefined && application?.brand !== null && application?.brand !== '') ||
                                                                (application?.price !== undefined && application?.price !== null && application?.price !== ''))
                                                                ? "col-6"
                                                                : "col-12"
                                                    }>
                                                        <p className="">
                                                            <label className="fw-normal">App. Amt</label>
                                                            <p className="text-body-secondary fw-semibold">
                                                                {application?.approval_amt}
                                                            </p>
                                                        </p>
                                                    </div>
                                                )}
                                                {/* Brand column */}
                                                {application?.brand !== undefined && application?.brand !== null && application?.brand !== '' && (
                                                    <div className={
                                                        (application?.approval_amt !== undefined && application?.approval_amt !== null && application?.approval_amt !== '') &&
                                                            (application?.price !== undefined && application?.price !== null && application?.price !== '')
                                                            ? "col-4 text-center"
                                                            : ((application?.approval_amt !== undefined && application?.approval_amt !== null && application?.approval_amt !== '') ||
                                                                (application?.price !== undefined && application?.price !== null && application?.price !== ''))
                                                                ? "col-6 text-center"
                                                                : "col-12 text-center"
                                                    }>
                                                        <p className="">
                                                            <label className="fw-normal">Brand</label>
                                                            <p className="text-body-secondary fw-semibold">
                                                                {capitalizeFirstLetter(application?.brand)}
                                                            </p>
                                                        </p>
                                                    </div>
                                                )}
                                                {/* Price column */}
                                                {application?.price !== undefined && application?.price !== null && application?.price !== '' && (
                                                    <div className={
                                                        (application?.approval_amt !== undefined && application?.approval_amt !== null && application?.approval_amt !== '') &&
                                                            (application?.brand !== undefined && application?.brand !== null && application?.brand !== '')
                                                            ? "col-4 text-end"
                                                            : ((application?.approval_amt !== undefined && application?.approval_amt !== null && application?.approval_amt !== '') ||
                                                                (application?.brand !== undefined && application?.brand !== null && application?.brand !== ''))
                                                                ? "col-6 text-end"
                                                                : "col-12 text-end"
                                                    }>
                                                        <p className="">
                                                            <label className="fw-normal">Price</label>
                                                            <p className="text-body-secondary fw-semibold">
                                                                {application?.price}
                                                            </p>
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="col-12 text-center">
                                <p className="text-secondary">
                                    {searchTerm ? `No applications found matching "${searchTerm}"` : "No applications found"}
                                </p>
                            </div>
                        )}

                        {hasMoreRecords && !isLoading && !loader && (
                            <div className="col-md-12 text-center">
                                <button
                                    className="dark-blue small load-more"
                                    onClick={loadMoreRecords}
                                >
                                    load more
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div >

            {/* Stores Modal */}
            < Modal
                id="SelectRole"
                show={showDealerModal}
                onClose={() => setShowDealerModal(false)}
                modalClass="modal-dialog bottom-modal"
                contentClass="blue"
            >
                <ModalHeader className="">
                    <div className="top-draw"></div>
                </ModalHeader>

                <div className="d-flex align-items-center justify-content-between px-3 py-3 w-100">
                    <h6 className="mb-1">Stores</h6>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowDealerModal(false)}
                        aria-label="Close"
                    ></button>
                </div>

                <ModalBody>
                    <ul className="list-group list-group-flush">
                        <li
                            className={`list-group-item border-0 text-secondary ${tempSelectedDealerCode === 'all' ? 'dealer-selected' : ''}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleDealerSelection('all', 'All')}
                            onKeyDown={(e) => e.key === 'Enter' && handleDealerSelection('all', 'All')}
                            tabIndex={0}
                            role="button"
                        >
                            All Stores
                        </li>
                        {dealerData.length > 0 ? (
                            dealerData.map((dealer, index) => (
                                <li
                                    key={index}
                                    className={`list-group-item border-0 text-secondary ${tempSelectedDealerCode === dealer.dealercode ? 'dealer-selected' : ''}`}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleDealerSelection(dealer.dealercode, dealer.dealername)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleDealerSelection(dealer.dealercode, dealer.dealername)}
                                    tabIndex={0}
                                    role="button"
                                >
                                    {dealer.dealercode} - {dealer.dealername}
                                </li>
                            ))
                        ) : (
                            <li className="list-group-item border-0 text-secondary">
                                No stores available
                            </li>
                        )}
                    </ul>
                </ModalBody>

                <ModalFooter>
                    <button
                        type="button"
                        className="btn btn-primary w-100"
                        onClick={handleApplyDealer}
                    >
                        Apply
                    </button>
                </ModalFooter>
            </Modal >

            {/* Search Modal */}
            < Modal
                id="searchrole"
                show={showSearchModal}
                onClose={() => setShowSearchModal(false)}
                modalClass="modal-dialog bottom-modal"
                contentClass="blue"
            >
                <ModalHeader>
                    <div className="top-draw"></div>
                </ModalHeader>

                <div className="d-flex align-items-center justify-content-between custom-lr-padding">
                    <h6 className="mb-1">Search</h6>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowSearchModal(false)}
                        aria-label="Close"
                    ></button>
                </div>

                <ModalBody className="searchrole">
                    <form className="col-12 col-lg-auto mb-3 mb-lg-0 me-lg-3 position-relative" role="search">
                        <input
                            type="search"
                            className="form-control input-underline"
                            placeholder="Search applications"
                            aria-label="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <div className="has-i-search hidden-search-box applications-search">
                            <span className="icon-searchnormal fs-4 inside-search border-0"></span>
                        </div>
                        <div className="row mb-3">
                            <div className="col-md-12 RightPane">
                                <div className="d-flex justify-content-between align-items-center py-2 mt-3">
                                    <ul className="nav nav-pills scroll-hidden d-none" id="pills-tab" role="tablist">
                                        <li className="nav-item px-2 py-2 current-primary" role="presentation">
                                            <i className="icon-settingh fw-bold"></i>
                                        </li>

                                        <li className="nav-item" role="presentation">
                                            <button className="nav-link" id="pills-appli-tab" data-bs-toggle="pill" data-bs-target="#pills-appli" type="button" role="tab" aria-controls="pills-appli" aria-selected="false" tabindex="-1">Date</button>
                                        </li>
                                        <li className="nav-item" role="presentation">
                                            <button className="nav-link active" id="pills-coappli-tab" data-bs-toggle="pill" data-bs-target="#pills-coappli" type="button" role="tab" aria-controls="pills-coappli" aria-selected="true">Dealer</button>
                                        </li>

                                        <li className="nav-item" role="presentation">
                                            <button className="nav-link " id="pills-date-tab" data-bs-toggle="pill" data-bs-target="#pills-date" type="button" role="tab" aria-controls="pills-date" aria-selected="true">Stage</button>
                                        </li>
                                        <li className="nav-item" role="presentation">
                                            <button className="nav-link " id="pills-prod-tab" data-bs-toggle="pill" data-bs-target="#pills-prod" type="button" role="tab" aria-controls="pills-prod" aria-selected="true">Products</button>
                                        </li>
                                        <li className="nav-item" role="presentation">
                                            <button className="nav-link " id="pills-price-tab" data-bs-toggle="pill" data-bs-target="#pills-price" type="button" role="tab" aria-controls="pills-price" aria-selected="true">Price</button>
                                        </li>
                                    </ul>

                                </div>
                            </div>
                        </div>
                    </form>
                </ModalBody>
            </Modal >

            {/* Profile Modal */}
            < Modal
                id="profilerole"
                show={showProfileModal}
                onClose={() => setShowProfileModal(false)}
                modalClass="modal-dialog bottom-modal"
                contentClass="blue"
            >
                <ModalHeader className="">
                    <div className="top-draw"></div>
                </ModalHeader>

                <div className="d-flex align-items-center justify-content-end px-4 pt-2 w-100">
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowProfileModal(false)}
                        aria-label="Close"
                    ></button>
                </div>

                <ModalBody>
                    <div className="row gy-4 p-1">
                        <div className="col-md-12">
                            <div className="d-flex align-items-center gap-2">
                                <ImageComponent fileName={"gender-icon.svg"} alt="profilepop" />
                                <div className="name fw-semibold">
                                    {userName}<br />
                                    <span className="text-secondary fw-normal">{emailID}</span>
                                </div>
                            </div>
                        </div>

                        <div className="col-md-12">
                            <div className="d-flex align-items-center justify-content-between gap-4">
                                <div className="d-flex gap-4 align-items-center">
                                    <i className="icon-notification01 fs-4 dark-blue"></i>{' '}
                                    Notifications
                                </div>
                                <div className="noti-count text-white">
                                    2
                                </div>
                            </div>
                        </div>

                        <div className="col-md-12">
                            <button
                                className="text-decoration-none text-black bg-white border-0 p-0"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleLogout();
                                }}
                            >
                                <div className="d-flex gap-4 align-items-center">
                                    <i className="icon-arrowlogout-E1 fs-4 dark-blue"></i>{' '}
                                    Logout
                                </div>
                            </button>
                        </div>
                    </div>
                </ModalBody>
            </Modal >

            {/* Filter Modal */}
            < Modal
                id="FilterModal"
                show={showDateModal}
                onClose={() => setShowDateModal(false)}
                modalClass="modal-dialog bottom-modal"
                contentClass="blue"
            >
                <ModalHeader className="">
                    <div className="top-draw"></div>
                </ModalHeader>

                <div className="d-flex align-items-center justify-content-between px-4 pt-4 w-100">
                    <h6 className="mb-1 text-dark">Date Selection</h6>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowDateModal(false)}
                        aria-label="Close"
                    ></button>
                </div>

                <ModalBody>
                    <ul className="list-group list-group-flush">
                        <div className="row gy-3">
                            <div className="col-6">
                                <DatePicker
                                    value={dateFrom}
                                    onChange={setDateFrom}
                                    label="Date From"
                                    required={true}
                                />
                            </div>
                            <div className="col-6">
                                <DatePicker
                                    value={dateTo}
                                    onChange={setDateTo}
                                    label="Date To"
                                    required={true}
                                />
                            </div>
                            <div className="col-md-12 d-none">
                                <label className="form-label" htmlFor='status'>Status</label>
                                <select
                                    className="form-select input-underline"
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                >
                                    {statusOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-sm-12 dashboard-date-top">
                                <div className="d-flex justify-content-end">
                                    <button
                                        className="btn btn-primary"
                                        type="button" onClick={handleProceedFilter}
                                    >
                                        Proceed
                                    </button>
                                </div>
                            </div>
                        </div>
                    </ul>
                </ModalBody>
            </Modal >
        </>
    )
}

ApplicationContent.propTypes = {
    searchTerm: PropTypes.string.isRequired,
    setSearchTerm: PropTypes.func.isRequired,
    showSearchModal: PropTypes.bool.isRequired,
    setShowSearchModal: PropTypes.func.isRequired
};

export default ApplicationContent